import { KeyValue } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  IAddressDto,
  INumberLoginDto,
  IRestaurantDto,
} from 'src/app/core/models';
import { CommonConstants } from '../../constant/common.constant';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { FileConstants } from '../../constant';
import { environment } from 'src/environments/environment';
import { BearerToken } from 'src/app/core/models/bearer.token';
import {
  CommonService,
  FileService,
  RestaurantService,
  ToastService,
  TokenService,
} from 'src/app/core/services';

@Component({
  selector: 'ic-add-restaurant',
  templateUrl: './add-restaurant.component.html',
  styleUrls: ['./add-restaurant.component.scss'],
})
export class AddRestaurantComponent implements OnInit {
  @Input() id: number = 0;
  @Input() profile: boolean;
  public client: BearerToken;
  public restaurant: IRestaurantDto;
  public restaurantForm: FormGroup;
  public mobileListFormArray: FormArray;
  public formErrors: any = {};
  public timeZoneList: KeyValue<number, string>[];
  public stateList: KeyValue<number, string>[];
  public cityList: KeyValue<number, string>[];
  public country = CommonConstants.Country;
  public address: IAddressDto;
  public isProd = environment.production;

  public imageChangedEventLogo: any;
  public croppedImageLogo: any;

  public croppedImageBackground: any;

  constructor(
    private readonly fb: FormBuilder,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly tokenService: TokenService,
    private readonly commonService: CommonService,
    private readonly restaurantService: RestaurantService,
    private readonly fileService: FileService,
  ) {
    this.client = this.tokenService.getToken();
  }

  ngOnInit(): void {
    this.getTimeZone();

    this.restaurantForm = this.fb.group({
      name: ['', [Validators.required]],
      ownerName: ['', [Validators.required]],
      description: [''],
      email: ['', [Validators.required, Validators.email]],
      mobile: ['', [Validators.required]],
      mobile2: [''],
      address: ['', [Validators.required]],
      countryId: [this.country.Usa, [Validators.required]],
      stateId: [null, [Validators.required]],
      cityId: [null, [Validators.required]],
      zipCode: ['', [Validators.required]],
      timeZoneId: [null, [Validators.required]],
      facebookLink: [''],
      webSiteLink: ['', [Validators.required]],
      tableOrderLink: [''],
      androidAppLink: [''],
      iosAppLink: [''],
      showMap: [false],
      showFacebook: [false],
      showAndroidStore: [false],
      showIosStore: [false],
      userCurrentLocation: [false],
      restaurantCoordinates: ['', [Validators.required]],
      googleAddress: ['', [Validators.required]],
      colorCode: ['#ff0000', [Validators.required]],
      isAndroidOrder: [true],
      isIosOrder: [true],
      mobileList: this.fb.array([]),
    });

    // This forces validation.
    Object.keys(this.restaurantForm.controls).forEach((key) => {
      this.restaurantForm.get(key).markAsTouched();
    });

    this.restaurantForm.valueChanges.subscribe((changes) => {
      Object.keys(changes).forEach((controlName) => {
        const control = this.restaurantForm.controls[controlName];
        this.formErrors[controlName] =
          (control.touched || control.dirty) && control.errors ? 'error' : null;
      });
    });

    if (this.id > 0) {
      this.getRestaurant();
    } else {
      this.address = {} as IAddressDto;
      this.validateControls();
      this.getStates();
    }
  }

  get fc() {
    return this.restaurantForm.controls;
  }

  changeColorCode(event: any) {
    this.restaurantForm.patchValue({
      colorCode: event.value,
    });
  }

  validateControls() {
    const fbCtrl = this.restaurantForm.get('facebookLink');
    const androidCtrl = this.restaurantForm.get('androidAppLink');
    const iosCtrl = this.restaurantForm.get('iosAppLink');

    if (this.restaurantForm.get('showFacebook').value) {
      fbCtrl.enable();
      fbCtrl.setValidators([
        Validators.required,
        // Validators.pattern(CommonConstants.urlRegex),
      ]);
    } else {
      fbCtrl.clearValidators();
      fbCtrl.disable();
    }

    if (this.restaurantForm.get('showAndroidStore').value) {
      androidCtrl.enable();
      androidCtrl.setValidators([
        Validators.required,
        // Validators.pattern(CommonConstants.urlRegex),
      ]);
    } else {
      androidCtrl.clearValidators();
      androidCtrl.disable();
    }

    if (this.restaurantForm.get('showIosStore').value) {
      iosCtrl.enable();
      iosCtrl.setValidators([
        Validators.required,
        // Validators.pattern(CommonConstants.urlRegex),
      ]);
    } else {
      iosCtrl.clearValidators();
      iosCtrl.disable();
    }

    fbCtrl.updateValueAndValidity();
    androidCtrl.updateValueAndValidity();
    iosCtrl.updateValueAndValidity();
  }

  addNewOption(model?: INumberLoginDto) {
    this.mobileListFormArray = this.restaurantForm.get(
      'mobileList',
    ) as FormArray;
    this.mobileListFormArray.push(this.createOptionItem(model));
  }

  createOptionItem(model: INumberLoginDto): FormGroup {
    if (!model) {
      model = {} as INumberLoginDto;
    }
    return this.fb.group({
      name: [model?.name, [Validators.required, Validators.minLength(1)]],
      mobile: [model?.mobile, [Validators.required, Validators.minLength(1)]],
    });
  }

  removeOption(index: number) {
    this.mobileListFormArray.removeAt(index);
  }

  removeLogo() {
    this.clearLogoImage();
    this.restaurantForm.get('isLogoImage').patchValue(false);
  }

  removeBackground() {
    this.clearBackgroundImage();
    this.restaurantForm.get('isBackgroundImage').patchValue(false);
  }

  onSave() {
    const model = {
      ...this.restaurantForm.getRawValue(),
    } as IRestaurantDto;

    model.id = this.id;

    model.isLogoImage = this.croppedImageLogo ? true : false;
    model.logo = this.croppedImageLogo;

    model.isBackgroundImage = this.croppedImageBackground ? true : false;
    model.backgroundImage = this.croppedImageBackground;

    // if (!model.mobileList || model.mobileList.length === 0) {
    //   this.toastService.showError('Please add at least one Mobile/Username');
    //   return;
    // }

    // let isValidUsername = true;
    // const integerRegex = /^-?\d+$/;
    // model.mobileList.forEach((x) => {
    //   if (x.toString().length !== 10 || !integerRegex.test(x.toString())) {
    //     isValidUsername = false;
    //     return;
    //   }
    // });
    // if (!isValidUsername) {
    //   this.toastService.showError('Invalid Mobile/Username');
    //   return;
    // }

    model.mobile = model.mobile.replace(/\D/g, '');

    this.restaurantService.saveRestaurant(model).subscribe(
      (data) => {
        if (data.isSuccess) {
          this.toastService.showSuccess('Updated Successfully!');
          if (this.id === 0) {
            this.onPrevious();
          }
        } else {
          this.toastService.showError(data.errors[0]);
        }
      },
      (err) => {
        this.toastService.showError(err);
      },
    );
  }

  getTimeZone() {
    this.commonService.getTimeZone().subscribe(
      (data) => {
        this.timeZoneList = data;
      },
      (err) => {
        this.toastService.showError(err);
      },
    );
  }

  getStates(id: number = 0) {
    this.stateList = [];
    this.cityList = [];
    if (id === 0) {
      this.restaurantForm.get('stateId').setValue(null);
      this.restaurantForm.get('cityId').setValue(null);
      id = this.restaurantForm.get('countryId').value;
    }
    if (!id) {
      return;
    }
    this.commonService.getState(id).subscribe(
      (data) => {
        this.stateList = data;
      },
      (err) => {
        this.toastService.showError(err);
      },
    );
  }

  getCities(id: number = 0) {
    this.cityList = [];
    if (id === 0) {
      this.restaurantForm.get('cityId').setValue(null);
      id = this.restaurantForm.get('stateId').value;
    }
    if (!id) {
      return;
    }
    this.commonService.getCity(id).subscribe(
      (data) => {
        this.cityList = data;
      },
      (err) => {
        this.toastService.showError(err);
      },
    );
  }

  googleAddressEvent(event: IAddressDto) {
    if (event) {
      const coordinates = `${event.latitude},${event.longitude}`;
      this.restaurantForm.get('googleAddress').patchValue(event.address);
      this.restaurantForm.get('restaurantCoordinates').patchValue(coordinates);
    } else {
      this.restaurantForm.get('googleAddress').patchValue(null);
      this.restaurantForm.get('restaurantCoordinates').patchValue(null);
    }
  }

  private getRestaurant() {
    this.restaurantService.getRestaurant(this.id).subscribe(
      (data) => {
        this.restaurant = data;
        this.bindRestaurant();
      },
      (err) => {
        this.toastService.showError(err);
      },
    );
  }

  private bindRestaurant() {
    this.checkImage(this.restaurant.logo, 1);
    this.checkImage(this.restaurant.backgroundImage, 2);

    this.restaurantForm.patchValue({
      name: this.restaurant.name,
      ownerName: this.restaurant.ownerName,
      description: this.restaurant.description,
      email: this.restaurant.email,
      mobile: this.restaurant.mobile,
      mobile2: this.restaurant.mobile2,
      address: this.restaurant.address,
      countryId: this.restaurant.countryId,
      stateId: this.restaurant.stateId,
      cityId: this.restaurant.cityId,
      zipCode: this.restaurant.zipCode,
      outletCoordinates: this.restaurant.restaurantCoordinates,
      googleAddress: this.restaurant.googleAddress,
      timeZoneId: this.restaurant.timeZoneId,
      webSiteLink: this.restaurant.webSiteLink,
      tableOrderLink: this.restaurant.tableOrderLink,
      showMap: this.restaurant.showMap,
      userCurrentLocation: this.restaurant.userCurrentLocation,
      showFacebook: this.restaurant.showFacebook,
      facebookLink: this.restaurant.facebookLink,
      showAndroidStore: this.restaurant.showAndroidStore,
      androidAppLink: this.restaurant.androidAppLink,
      showIosStore: this.restaurant.showIosStore,
      iosAppLink: this.restaurant.iosAppLink,
      colorCode: this.restaurant.colorCode,
      //mobileList: this.restaurant.mobileList,
      isAndroidOrder: this.restaurant.isAndroidOrder,
      isIosOrder: this.restaurant.isIosOrder,
    });

    if (this.restaurant.restaurantCoordinates) {
      const coordinates = this.restaurant?.restaurantCoordinates.split(',');
      if (coordinates && coordinates.length == 2) {
        this.address = {
          latitude: Number(coordinates[0]),
          longitude: Number(coordinates[1]),
          address: this.restaurant.googleAddress,
        } as IAddressDto;
      } else {
        this.address = {} as IAddressDto;
      }
    } else {
      this.address = {} as IAddressDto;
    }

    if (this.restaurant.mobileList?.length > 0) {
      this.restaurant.mobileList.forEach((x: INumberLoginDto) => {
        this.addNewOption(x);
      });
    } else {
      //this.addNewOption();
    }

    if (
      this.client.roles !==
      CommonConstants.Role[CommonConstants.Role.Administrator]
    ) {
      this.restaurantForm.get('email').disable();
    }
    this.getStates(this.restaurant.countryId);
    this.getCities(this.restaurant.stateId);
    this.validateControls();
  }

  checkImage(url: string, type: number): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        type == 1
          ? (this.restaurant.isLogoImage = true)
          : (this.restaurant.isBackgroundImage = true);
        resolve(true);
      };

      img.onerror = () => {
        type == 1
          ? (this.restaurant.isLogoImage = false)
          : (this.restaurant.isBackgroundImage = false);
        resolve(false);
      };

      img.src = url + '?t=' + new Date().getTime();
    });
  }

  bindDomain() {
    this.commonService.bindDomain(this.id).subscribe(
      (data) => {
        if (data.isSuccess) {
          this.toastService.showSuccess('Custom Domain added successfully! ');
        } else {
          this.toastService.showError(data.errors[0]);
        }
      },
      (err) => {
        this.toastService.showError(err);
      },
    );
  }

  onPrevious() {
    this.router.navigate(['home/restaurant']);
  }

  fileChangeEventLogo(event: any): void {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    const fileExt = `.${file.name.replace(/^.*\./, '')}`;
    // const fileExt = file.name.substr((file.name.lastIndexOf('.') + 1));

    if (!this.fileService.isValidAttachmentExtension(fileExt)) {
      this.toastService.showError('Please upload valid file types');
      this.clearLogoImage();
      event.preventDefault();
      return;
    }

    if (!this.fileService.isValidSize(event.target.files)) {
      this.toastService.showError(FileConstants.fileSizeErrorMsg);
      this.clearLogoImage();
      event.preventDefault();
      return;
    }
    this.imageChangedEventLogo = event;
  }

  imageCroppedLogo(event: ImageCroppedEvent) {
    this.croppedImageLogo = event.base64;
  }

  loadImageFailedLogo() {
    this.croppedImageLogo = null;
    this.toastService.showError(
      'Logo Image uploading failed! Please try again',
    );
  }

  fileChangeEventBackground(event: any): void {
    const file = event.target.files[0];
    if (!file) {
      this.clearBackgroundImage();
      return;
    }
    const fileExt = `.${file.name.replace(/^.*\./, '')}`;

    if (!this.fileService.isValidAttachmentExtension(fileExt)) {
      this.toastService.showError('Please upload valid file types');
      this.clearBackgroundImage();
      event.preventDefault();
      return;
    }

    if (!this.fileService.isValidSize(event.target.files)) {
      this.toastService.showError(FileConstants.fileSizeErrorMsg);
      this.clearBackgroundImage();
      event.preventDefault();
      return;
    }

    var reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      this.croppedImageBackground = event.target.result;
    };
  }

  // dataURLtoFile(dataUrl: string, fileName: string) {
  //   var arr = dataUrl.split(','),
  //     mime = arr[0].match(/:(.*?);/)[1],
  //     bstr = atob(arr[1]),
  //     n = bstr.length,
  //     u8arr = new Uint8Array(n);
  //   while (n--) {
  //     u8arr[n] = bstr.charCodeAt(n);
  //   }
  //   return new File([u8arr], fileName, { type: mime });
  // }

  imageLoaded() {}

  cropperReady() {}

  clearLogoImage() {
    this.croppedImageLogo = null;
    this.imageChangedEventLogo = null;
  }

  clearBackgroundImage() {
    this.croppedImageBackground = null;
  }
}
